import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Typography, 
  Button, 
  Space,
  Table,
  Tag,
  Progress
} from 'antd';
import { 
  UserOutlined, 
  FileTextOutlined, 
  TeamOutlined, 
  EyeOutlined,
  PlusOutlined,
  ArrowUpOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const { Title } = Typography;

interface DashboardStats {
  totalJobPostings: number;
  activeJobPostings: number;
  totalApplications: number;
  pendingApplications: number;
  acceptedApplications: number;
  rejectedApplications: number;
}

interface RecentApplication {
  id: number;
  jobTitle: string;
  applicantName: string;
  status: 'pending' | 'reviewed' | 'accepted' | 'rejected';
  appliedAt: string;
}

const CompanyDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>({
    totalJobPostings: 0,
    activeJobPostings: 0,
    totalApplications: 0,
    pendingApplications: 0,
    acceptedApplications: 0,
    rejectedApplications: 0
  });

  const [recentApplications, setRecentApplications] = useState<RecentApplication[]>([]);

  // Mock data - replace with actual API calls
  useEffect(() => {
    setStats({
      totalJobPostings: 12,
      activeJobPostings: 8,
      totalApplications: 45,
      pendingApplications: 15,
      acceptedApplications: 20,
      rejectedApplications: 10
    });

    setRecentApplications([
      {
        id: 1,
        jobTitle: 'Frontend Developer',
        applicantName: 'Ahmet Yılmaz',
        status: 'pending',
        appliedAt: '2024-01-20'
      },
      {
        id: 2,
        jobTitle: 'Backend Developer',
        applicantName: 'Ayşe Demir',
        status: 'reviewed',
        appliedAt: '2024-01-19'
      },
      {
        id: 3,
        jobTitle: 'UI/UX Designer',
        applicantName: 'Mehmet Kaya',
        status: 'accepted',
        appliedAt: '2024-01-18'
      }
    ]);
  }, []);

  const recentApplicationsColumns = [
    {
      title: 'Başvuran',
      dataIndex: 'applicantName',
      key: 'applicantName',
    },
    {
      title: 'İlan',
      dataIndex: 'jobTitle',
      key: 'jobTitle',
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusConfig = {
          pending: { color: 'orange', text: 'Beklemede' },
          reviewed: { color: 'blue', text: 'İncelendi' },
          accepted: { color: 'green', text: 'Kabul Edildi' },
          rejected: { color: 'red', text: 'Reddedildi' }
        };
        const config = statusConfig[status as keyof typeof statusConfig];
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: 'Tarih',
      dataIndex: 'appliedAt',
      key: 'appliedAt',
      render: (date: string) => new Date(date).toLocaleDateString('tr-TR'),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: any, record: RecentApplication) => (
        <Button 
          type="link" 
          icon={<EyeOutlined />} 
          size="small"
          onClick={() => navigate('/company/panel')}
        >
          Görüntüle
        </Button>
      ),
    },
  ];

  const acceptanceRate = stats.totalApplications > 0 
    ? Math.round((stats.acceptedApplications / stats.totalApplications) * 100) 
    : 0;

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>Dashboard</Title>
        <Title level={4} type="secondary">Şirket panelinize hoş geldiniz</Title>
      </div>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Toplam İlan"
              value={stats.totalJobPostings}
              prefix={<FileTextOutlined />}
              suffix={
                <span style={{ color: 'var(--success)', fontSize: '12px' }}>
                  <ArrowUpOutlined /> +2
                </span>
              }
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Aktif İlan"
              value={stats.activeJobPostings}
              prefix={<EyeOutlined />}
              valueStyle={{ color: 'var(--primary-500)' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Toplam Başvuru"
              value={stats.totalApplications}
              prefix={<TeamOutlined />}
              suffix={
                <span style={{ color: 'var(--success)', fontSize: '12px' }}>
                  <ArrowUpOutlined /> +5
                </span>
              }
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Bekleyen Başvuru"
              value={stats.pendingApplications}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        {/* Recent Applications */}
        <Col xs={24} lg={16}>
          <Card 
            title="Son Başvurular"
            extra={
              <Button 
                type="primary" 
                onClick={() => navigate('/company/panel')}
              >
                Tümünü Gör
              </Button>
            }
          >
            <Table
              scroll={{ x: 'max-content' }}
              columns={recentApplicationsColumns}
              dataSource={recentApplications}
              rowKey="id"
              pagination={false}
              size="small"
            />
          </Card>
        </Col>

        {/* Quick Stats */}
        <Col xs={24} lg={8}>
          <Card title="Hızlı İstatistikler" style={{ marginBottom: '16px' }}>
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span>Kabul Oranı</span>
                <span>{acceptanceRate}%</span>
              </div>
              <Progress 
                percent={acceptanceRate} 
                strokeColor="var(--success)"
                size="small"
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span>Red Oranı</span>
                <span>{100 - acceptanceRate}%</span>
              </div>
              <Progress 
                percent={100 - acceptanceRate} 
                strokeColor="var(--error)"
                size="small"
              />
            </div>

            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span>İlan Doldurma</span>
                <span>{Math.round((stats.activeJobPostings / stats.totalJobPostings) * 100)}%</span>
              </div>
              <Progress 
                percent={Math.round((stats.activeJobPostings / stats.totalJobPostings) * 100)} 
                strokeColor="var(--primary-500)"
                size="small"
              />
            </div>
          </Card>

          <Card title="Hızlı İşlemler">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button 
                type="primary" 
                icon={<PlusOutlined />} 
                block
                onClick={() => navigate('/company-panel')}
              >
                Yeni İlan Oluştur
              </Button>
              <Button 
                icon={<TeamOutlined />} 
                block
                onClick={() => navigate('/company-panel')}
              >
                Başvuruları İncele
              </Button>
              <Button 
                icon={<FileTextOutlined />} 
                block
                onClick={() => navigate('/company-panel')}
              >
                İlanları Yönet
              </Button>
              <Button 
                icon={<UserOutlined />} 
                block
                onClick={() => navigate('/company-panel')}
              >
                Şirket Bilgileri
              </Button>
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default CompanyDashboard;